
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { MapPin, Clock, Battery, Gauge, Wifi, Loader2, Database, Signal } from 'lucide-react';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { LocationPoint } from './utils/locationHistoryUtils';

interface LastLocationDisplayProps {
  locationHistory: LocationPoint[];
  onSyncGPS?: () => Promise<void>;
  isSyncing?: boolean;
  bikeId?: string;
  courierId?: string;
}

const LastLocationDisplay: React.FC<LastLocationDisplayProps> = ({ 
  locationHistory, 
  onSyncGPS,
  isSyncing = false,
  bikeId,
  courierId
}) => {
  const [address, setAddress] = useState<string>('Получение адреса...');
  
  // Получаем последнюю точку
  const lastLocation = locationHistory && locationHistory.length > 0 
    ? locationHistory[locationHistory.length - 1] 
    : null;

  // ИСПРАВЛЕННАЯ ЛОГИКА: показываем компонент если есть хотя бы одна точка истории
  const shouldShow = lastLocation && locationHistory.length > 0;

  // Функция для получения адреса по координатам
  const getAddressFromCoordinates = async (lat: number, lng: number) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&accept-language=ru`
      );
      const data = await response.json();
      
      if (data && data.display_name) {
        return data.display_name;
      } else {
        return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
      }
    } catch (error) {
      console.error('Ошибка при получении адреса:', error);
      return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
    }
  };

  // Получаем адрес при изменении последней точки или данных истории
  useEffect(() => {
    if (lastLocation && shouldShow) {
      console.log('Обновляем адрес для координат:', lastLocation.latitude, lastLocation.longitude);
      setAddress('Получение адреса...');
      getAddressFromCoordinates(lastLocation.latitude, lastLocation.longitude)
        .then(newAddress => {
          console.log('Получен новый адрес:', newAddress);
          setAddress(newAddress);
        });
    }
  }, [lastLocation, lastLocation?.latitude, lastLocation?.longitude, lastLocation?.timestamp, locationHistory.length, shouldShow]);

  // Не показываем компонент, если условия не выполнены
  if (!shouldShow) {
    return null;
  }

  const formatTimestamp = (timestamp: string | Date): string => {
    const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
    return format(date, 'dd.MM.yyyy HH:mm:ss', { locale: ru });
  };

  // Определяем тип источника данных
  const isCurrentState = lastLocation.data_source === 'current_state';

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Последнее местоположение
          </CardTitle>
          
          <div className="flex items-center gap-2">
            {onSyncGPS && (
              <Button 
                onClick={onSyncGPS}
                disabled={isSyncing}
                variant="outline"
                size="sm"
                className="flex items-center gap-2"
              >
                {isSyncing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Wifi className="h-4 w-4" />
                )}
                {isSyncing ? 'Загрузка...' : 'Обновить GPS'}
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Время */}
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Время:</span>
          <span className="font-medium">{formatTimestamp(lastLocation.timestamp)}</span>
        </div>

        {/* Адрес */}
        <div className="flex items-start gap-2">
          <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
          <div className="flex-1">
            <span className="text-sm text-muted-foreground">Адрес:</span>
            <p className="font-medium">{address}</p>
          </div>
        </div>

        {/* Дополнительная информация */}
        <div className="grid grid-cols-2 gap-4">
          {lastLocation.battery_level !== undefined && (
            <div className="flex items-center gap-2">
              <Battery className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Заряд:</span>
              <span className="font-medium">{lastLocation.battery_level}%</span>
            </div>
          )}
          
          {/* Статус подключения - показываем только если доступен */}
          {lastLocation.online !== undefined && (
            <div className="flex items-center gap-2">
              <Signal className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Статус:</span>
              <span className={`font-medium ${lastLocation.online ? 'text-green-600' : 'text-red-600'}`}>
                {lastLocation.online ? 'Онлайн' : 'Оффлайн'}
              </span>
            </div>
          )}
        </div>

        {/* Информационное сообщение только для текущего состояния */}
        {isCurrentState && (
          <div className="mt-3 p-2 bg-blue-50 dark:bg-blue-900/20 rounded-md">
            <p className="text-xs text-blue-700 dark:text-blue-300">
              <Database className="h-3 w-3 inline mr-1" />
              Данные получены из текущего состояния велосипеда (нет истории перемещений за выбранный период)
            </p>
          </div>
        )}

        {/* Координаты */}
        <div className="text-xs text-muted-foreground">
          Координаты: {lastLocation.latitude.toFixed(6)}, {lastLocation.longitude.toFixed(6)}
        </div>
      </CardContent>
    </Card>
  );
};

export default LastLocationDisplay;
